/**
 * KPC membership link: App Proxy validation API and UI updates.
 * Submit handler uses capture phase so it runs before the addresses section bubble listener.
 */
(function () {
  'use strict';

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  var DEFAULT_KPC_NUMBER_DIGITS = 6;

  function getKpcNumberDigits(form) {
    if (!form) return DEFAULT_KPC_NUMBER_DIGITS;
    var raw = form.getAttribute('data-kpc-number-digits');
    var digits = parseInt(raw, 10);
    return digits > 0 ? digits : DEFAULT_KPC_NUMBER_DIGITS;
  }

  function isValidKpcNumberFormat(kpcNumber, digits) {
    var length = digits || DEFAULT_KPC_NUMBER_DIGITS;
    return new RegExp('^\\d{' + length + '}$').test(kpcNumber);
  }

  function buildExpirationDate(day, month, year) {
    if (!year || !month || !day) return '';
    return year + '-' + pad2(month) + '-' + pad2(day);
  }

  function isValidExpiryDate(day, month, year) {
    var d = parseInt(day, 10);
    var m = parseInt(month, 10);
    var y = parseInt(year, 10);
    if (!d || !m || !y) {
      return false;
    }
    var currentYear = new Date().getFullYear();
    if (y < currentYear || y > currentYear + 3) {
      return false;
    }
    var date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
  }

  /**
   * Client-side checks before API: presence, digit format, calendar date.
   * @returns {{ valid: true } | { valid: false, code: string, place: 'top'|'field', target: 'all'|'kpc-only' }}
   */
  function validateKpcFormFields(kpcNumber, day, month, year, digits) {
    if (!kpcNumber || !isValidKpcNumberFormat(kpcNumber, digits)) {
      return { valid: false, code: 'digit_length', place: 'field', target: 'kpc-only' };
    }
    if (!day || !month || !year) {
      return { valid: false, code: 'invalid', place: 'top', target: 'all' };
    }
    if (!isValidExpiryDate(day, month, year)) {
      return { valid: false, code: 'invalid', place: 'top', target: 'all' };
    }
    return { valid: true };
  }

  var KPC_APP_PROXY_PATH = '/apps/kpc_validate';

  function buildKpcRequestPayload(kpcNumber, expirationDate) {
    return new URLSearchParams({
      kpc_number: kpcNumber,
      expiration_date: expirationDate,
    });
  }

  /**
   * Always call the storefront app proxy (same-origin).
   * Send card fields as query params and urlencoded POST body — app backends often
   * read req.query / req.body form fields but not JSON through the proxy.
   */
  function resolveKpcApiUrl(configuredUrl, payload) {
    var path = KPC_APP_PROXY_PATH;
    if (
      configuredUrl &&
      configuredUrl.indexOf('/') === 0 &&
      configuredUrl.indexOf('/apps/') === 0
    ) {
      path = configuredUrl.split('?')[0];
    }
    return path + '?' + payload.toString();
  }

  function formMsg(form, suffix) {
    return (form && form.getAttribute('data-kpc-msg-' + suffix)) || '';
  }

  function buildExpiredFieldHtml(form) {
    var url = (form && form.getAttribute('data-kpc-renewal-url')) || '';
    var before = formMsg(form, 'expired-before');
    var linkText = formMsg(form, 'expired-link');
    var after = formMsg(form, 'expired-after');
    if (!url) {
      return before + linkText + after;
    }
    return (
      before +
      '<a href="' +
      String(url).replace(/"/g, '') +
      '" target="_blank" rel="noopener noreferrer" class="kpc-membership-form__error-link">' +
      linkText +
      '</a>' +
      after
    );
  }

  function isKpcMembershipForm(form) {
    if (!form || form.tagName !== 'FORM') {
      return false;
    }
    if (form.id === 'kpc-membership-form') {
      return true;
    }
    if (form.hasAttribute && form.hasAttribute('data-kpc-membership-form')) {
      return true;
    }
    if (form.classList && form.classList.contains('kpc-membership-form')) {
      return true;
    }
    if (form.closest && form.closest('.modal__content--kpc')) {
      return true;
    }
    return false;
  }

  function parseApiPayload(response, data) {
    return { response: response, data: data };
  }

  function kpcApiResponseText(data) {
    var parts = [];
    if (data && data.message !== undefined) {
      parts.push(String(data.message));
    }
    if (data && data.error !== undefined) {
      parts.push(String(data.error));
    }
    return parts.join(' ').toLowerCase();
  }

  /**
   * API business error code. Expected values: invalid | expired | inactive | already_synced
   */
  function kpcApiCode(data) {
    if (!data) {
      return '';
    }
    if (data.code !== undefined && data.code !== null) {
      return String(data.code).toLowerCase();
    }
    if (data.error_code !== undefined && data.error_code !== null) {
      return String(data.error_code).toLowerCase();
    }
    return '';
  }

  /** HTTP 200 inactive responses use top-level status (not code). */
  function kpcApiStatus(data) {
    if (!data || data.status === undefined || data.status === null) {
      return '';
    }
    return String(data.status).toLowerCase();
  }

  function kpcMemberStatus(data) {
    if (!data || !data.member || data.member.status === undefined || data.member.status === null) {
      return '';
    }
    return String(data.member.status).toLowerCase();
  }

  function kpcOutcomeFromBusinessKey(key) {
    if (key === 'invalid') {
      return {
        kind: 'error',
        code: 'invalid',
        place: 'top',
        target: 'all',
      };
    }
    if (key === 'expired') {
      return {
        kind: 'error',
        code: 'expired',
        place: 'field',
        target: 'kpc-only',
      };
    }
    if (key === 'inactive') {
      return {
        kind: 'error',
        code: 'inactive',
        place: 'field',
        target: 'kpc-only',
      };
    }
    if (key === 'already_synced') {
      return {
        kind: 'error',
        code: 'already_synced',
        place: 'field',
        target: 'kpc-only',
      };
    }
    return null;
  }

  function kpcBusinessErrorOutcome(data) {
    if (isKpcAlreadySyncedFromErrorPayload(data)) {
      return kpcOutcomeFromBusinessKey('already_synced');
    }
    var fromCode = kpcOutcomeFromBusinessKey(kpcApiCode(data));
    if (fromCode) {
      return fromCode;
    }
    var fromStatus = kpcOutcomeFromBusinessKey(kpcApiStatus(data));
    if (fromStatus) {
      return fromStatus;
    }
    var fromMemberStatus = kpcOutcomeFromBusinessKey(kpcMemberStatus(data));
    if (fromMemberStatus) {
      return fromMemberStatus;
    }
    // API code may be "temporary" while Eber payload indicates wrong card details.
    if (isKpcInvalidFromErrorPayload(data)) {
      return kpcOutcomeFromBusinessKey('invalid');
    }
    return null;
  }

  // TODO(WRP-975): Eber APIが専用のerror_codeを返していないため、
  // message文字列による暫定判定。
  // Eber側でerror_code（例: ALREADY_SYNCED）が付与された時点で
  // kpcOutcomeFromBusinessKey のマッピングへ移すこと。
  // Eber側の文言変更で本判定が機能しなくなり、
  // KPC_TEMPORARY_OUTCOME（error_temporary）にフォールバックする。
  // 参照: WREBER-1 (intage.backlog.jp) 2026/08/06 Jason氏回答
  // レスポンス: 400 { "ok": false, "message": "Customer is synced to another user." }
  function isKpcAlreadySyncedFromErrorPayload(data) {
    // kpcApiResponseText lowercases; it does not trim — trim here for safety.
    var combined = kpcApiResponseText(data).trim();
    return combined.indexOf('synced to another user') !== -1;
  }

  function isKpcInvalidFromErrorPayload(data) {
    var combined = kpcApiResponseText(data);
    return (
      combined.indexOf('member not found') !== -1 ||
      combined.indexOf('kpc number is invalid') !== -1 ||
      combined.indexOf('expiration date is not match') !== -1 ||
      combined.indexOf('expiration date does not match') !== -1
    );
  }

  function isKpcBusinessErrorState(data) {
    return !!kpcBusinessErrorOutcome(data);
  }

  function isKpcTemporaryError(response, data) {
    if (response.status === 429) {
      return true;
    }
    if (response.status >= 500) {
      return true;
    }
    var combined = kpcApiResponseText(data);
    return (
      combined.indexOf('rate limit') !== -1 ||
      combined.indexOf('too many requests') !== -1
    );
  }

  function isKpcMissingFieldsError(data) {
    var combined = kpcApiResponseText(data);
    return combined.indexOf('missing input fields') !== -1 || combined.indexOf('missing field') !== -1;
  }

  function isKpcAppProxyConfigError(response, data) {
    if (response.status !== 422) {
      return false;
    }
    var combined = kpcApiResponseText(data);
    return (
      combined.indexOf('logged_in_customer_id') !== -1 ||
      combined.indexOf('logged in customer id') !== -1 ||
      combined.indexOf('signature') !== -1 ||
      combined.indexOf('timestamp') !== -1
    );
  }

  function isKpcApiFailure(response, data) {
    if (!response.ok) {
      return true;
    }
    if (data && data.ok === false) {
      return true;
    }
    if (data && data.success === false) {
      return true;
    }
    return false;
  }

  function isKpcApiSuccess(response, data) {
    if (!response.ok) {
      return false;
    }
    if (!data || data._raw !== undefined) {
      return false;
    }
    if (isKpcBusinessErrorState(data)) {
      return false;
    }
    if (data.ok === true || data.success === true || data.valid === true) {
      return true;
    }
    if (data.ok === false || data.success === false || data.valid === false) {
      return false;
    }
    return false;
  }

  var KPC_TEMPORARY_OUTCOME = {
    kind: 'error',
    code: 'temporary',
    place: 'field',
    target: 'kpc-only',
  };

  /**
   * Maps HTTP + JSON body to a single UI instruction (or success).
   * Business errors: code (invalid | expired), status (top-level or member.status),
   * (e.g. code "temporary" + "Member not found." → invalid UI).
   * Network / proxy / unknown failures use temporary copy.
   * @returns {{ kind: 'success' } | { kind: 'error', code: string, place: 'top'|'field', target: 'all'|'kpc-only' }}
   */
  function resolveKpcOutcome(response, data) {
    if (isKpcMissingFieldsError(data)) {
      return KPC_TEMPORARY_OUTCOME;
    }
    if (isKpcAppProxyConfigError(response, data)) {
      return KPC_TEMPORARY_OUTCOME;
    }
    if (isKpcTemporaryError(response, data)) {
      return KPC_TEMPORARY_OUTCOME;
    }

    var businessOutcome = kpcBusinessErrorOutcome(data);
    if (businessOutcome) {
      return businessOutcome;
    }

    if (isKpcApiFailure(response, data)) {
      return KPC_TEMPORARY_OUTCOME;
    }
    if (!isKpcApiSuccess(response, data)) {
      return KPC_TEMPORARY_OUTCOME;
    }
    return { kind: 'success' };
  }

  function parseResponseText(text) {
    if (!text) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      return { _raw: text };
    }
  }

  function readJsonOrRaw(response) {
    return response.text().then(function (text) {
      return parseResponseText(text);
    });
  }

  function showKpcError(form, code, place, target) {
    var errorElTop = form.querySelector('.kpc-membership-form__error--top');
    var errorElField = form.querySelector('.kpc-membership-form__error--field');
    var kpcInputEl = form.querySelector('#kpc-membership-number');
    var selectWrapEls = form.querySelectorAll('.kpc-membership-form__select-wrap');

    if (place === 'top' && errorElTop) {
      var textEl = errorElTop.querySelector('.kpc-membership-form__error-text');
      var topMsg = code === 'invalid' ? formMsg(form, 'invalid') : formMsg(form, 'temporary');
      if (textEl) textEl.textContent = topMsg;
      errorElTop.style.display = 'flex';
    }
    if (place === 'field' && errorElField) {
      if (code === 'expired') {
        errorElField.innerHTML = buildExpiredFieldHtml(form);
      } else if (code === 'inactive') {
        errorElField.textContent = formMsg(form, 'inactive');
      } else if (code === 'already_synced') {
        errorElField.textContent = formMsg(form, 'already-synced');
      } else if (code === 'digit_length') {
        errorElField.textContent = formMsg(form, 'digit-length');
      } else {
        errorElField.textContent = formMsg(form, 'temporary');
      }
      errorElField.style.display = 'block';
    }
    if (place === 'top' && errorElField) errorElField.style.display = 'none';
    if (place === 'field' && errorElTop) errorElTop.style.display = 'none';

    if (target === 'all') {
      if (kpcInputEl) {
        kpcInputEl.classList.remove('kpc-membership-form__input--error-field');
        kpcInputEl.classList.add('kpc-membership-form__input--error');
      }
      selectWrapEls.forEach(function (w) {
        w.classList.add('kpc-membership-form__select-wrap--error');
      });
    } else if (target === 'kpc-only' && kpcInputEl) {
      kpcInputEl.classList.remove('kpc-membership-form__input--error');
      kpcInputEl.classList.add('kpc-membership-form__input--error-field');
      selectWrapEls.forEach(function (w) {
        w.classList.remove('kpc-membership-form__select-wrap--error');
      });
    }
  }

  function clearKpcError(form) {
    var errorElTop = form.querySelector('.kpc-membership-form__error--top');
    var errorElField = form.querySelector('.kpc-membership-form__error--field');
    var kpcInputEl = form.querySelector('#kpc-membership-number');
    var selectWrapEls = form.querySelectorAll('.kpc-membership-form__select-wrap');
    if (errorElTop) {
      var textEl = errorElTop.querySelector('.kpc-membership-form__error-text');
      if (textEl) textEl.textContent = '';
      errorElTop.style.display = 'none';
    }
    if (errorElField) {
      errorElField.textContent = '';
      errorElField.style.display = 'none';
    }
    if (kpcInputEl) {
      kpcInputEl.classList.remove('kpc-membership-form__input--error');
      kpcInputEl.classList.remove('kpc-membership-form__input--error-field');
    }
    selectWrapEls.forEach(function (w) {
      w.classList.remove('kpc-membership-form__select-wrap--error');
    });
  }

  var KPC_SUCCESS_TOAST_MS = 3000;

  function showKpcSuccessToast() {
    var toast = document.getElementById('kpc-success-toast');
    if (!toast) {
      return;
    }
    if (toast.parentNode !== document.body) {
      document.body.appendChild(toast);
    }
    toast.classList.remove('kpc-success-toast--visible');
    void toast.offsetHeight;
    requestAnimationFrame(function () {
      toast.classList.add('kpc-success-toast--visible');
    });
    window.clearTimeout(window._kpcToastTimeout);
    window._kpcToastTimeout = window.setTimeout(function () {
      toast.classList.remove('kpc-success-toast--visible');
    }, KPC_SUCCESS_TOAST_MS);
  }

  function applyKpcSuccessUi(kpcNumber, year, month, day, form) {
    document.dispatchEvent(new CustomEvent('modal:close'));
    showKpcSuccessToast();
    var cardUnlinked = document.querySelector('[data-kpc-card-unlinked]');
    var cardLinked = document.querySelector('[data-kpc-card-linked]');
    var learnMore = document.querySelector('[data-kpc-card-learn-more]');
    var numberEl = document.querySelector('[data-kpc-number]');
    var expiryEl = document.querySelector('[data-kpc-expiry]');
    if (cardUnlinked) cardUnlinked.style.display = 'none';
    if (cardLinked) cardLinked.style.display = 'block';
    if (learnMore) learnMore.style.display = 'none';
    if (numberEl) numberEl.textContent = kpcNumber || '';
    if (expiryEl && year && month && day) {
      var expDate = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      var locale =
        (form && form.getAttribute('data-kpc-date-locale')) ||
        (document.documentElement && document.documentElement.lang) ||
        undefined;
      expiryEl.textContent = expDate.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  }

  function handleKpcSubmit(form, e) {
    e.preventDefault();
    e.stopPropagation();

    var kpcNumberInput = form.querySelector('#kpc-membership-number');
    var daySelect = form.querySelector('[name="kpc_expiry_day"]');
    var monthSelect = form.querySelector('[name="kpc_expiry_month"]');
    var yearSelect = form.querySelector('[name="kpc_expiry_year"]');

    var kpcNumber = kpcNumberInput ? kpcNumberInput.value.trim() : '';
    var day = daySelect ? daySelect.value : '';
    var month = monthSelect ? monthSelect.value : '';
    var year = yearSelect ? yearSelect.value : '';
    var expirationDate = buildExpirationDate(day, month, year);

    var validation = validateKpcFormFields(
      kpcNumber,
      day,
      month,
      year,
      getKpcNumberDigits(form)
    );
    if (!validation.valid) {
      showKpcError(form, validation.code, validation.place, validation.target);
      return;
    }

    var configuredApiUrl = form.getAttribute('data-api-url');
    var payload = buildKpcRequestPayload(kpcNumber, expirationDate);
    var encodedBody = payload.toString();
    var apiUrl = resolveKpcApiUrl(configuredApiUrl, payload);

    clearKpcError(form);

    fetch(apiUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: encodedBody,
    })
      .then(function (response) {
        return readJsonOrRaw(response).then(function (data) {
          return parseApiPayload(response, data);
        });
      })
      .then(function (payload) {
        var response = payload.response;
        var data = payload.data;

        var formEl = document.getElementById('kpc-membership-form');
        var activeForm = formEl || form;

        var outcome = resolveKpcOutcome(response, data);
        if (outcome.kind === 'error') {
          showKpcError(activeForm, outcome.code, outcome.place, outcome.target);
          return data;
        }

        clearKpcError(activeForm);
        applyKpcSuccessUi(kpcNumber, year, month, day, activeForm);
        return data;
      })
      .catch(function () {
        var formEl = document.getElementById('kpc-membership-form');
        var f = formEl || form;
        showKpcError(f, 'temporary', 'field', 'kpc-only');
      });
  }

  document.addEventListener(
    'submit',
    function (e) {
      var t = e.target;
      var form = t && t.tagName === 'FORM' ? t : t && t.closest ? t.closest('form') : null;
      if (!form || !isKpcMembershipForm(form)) {
        return;
      }
      handleKpcSubmit(form, e);
    },
    true
  );
})();
